// The room transport's record button, and the two settings a take depends on.
//
// Presentation only — useRoomRecorder decides everything. The settings panel
// is fixed-positioned off its button rather than absolutely inside it: the
// desktop transport scrolls sideways on a narrow window, and a panel inside a
// scroller is clipped to it.

import { useEffect, useRef, useState, type CSSProperties } from "react";
import { ChevronDown, Circle, Loader2 } from "lucide-react";
import { cn } from "@web-shared/lib/utils";
import { cleanTrackName } from "@/lib/track-name";
import type { RoomRecorder } from "../hooks/useRoomRecorder";

export function RecordButton({ rec, className }: { rec: RoomRecorder; className?: string }) {
  const lane = rec.target ? cleanTrackName(rec.target.name) : null;
  const title = rec.recording
    ? "Stop recording — the take lands as a clip"
    : rec.arming
      ? "Starting…"
      : lane
        ? `Record the mic onto ${lane} from the playhead`
        : "Arm an audio lane (●), or select one, to record onto it";
  return (
    <button
      type="button"
      onClick={rec.toggle}
      disabled={rec.arming || rec.saving}
      aria-pressed={rec.recording}
      aria-label={rec.recording ? "Stop recording" : "Record"}
      title={title}
      className={cn(
        "flex shrink-0 items-center justify-center rounded-lg disabled:opacity-60",
        rec.recording ? "bg-removed/20" : "hover:bg-white/5",
        className,
      )}
    >
      {rec.saving ? (
        <Loader2 className="size-4 animate-spin text-removed" />
      ) : (
        <Circle
          className={cn(
            "size-4 text-removed",
            (rec.recording || rec.arming) && "fill-removed",
            rec.arming && "animate-pulse",
          )}
        />
      )}
    </button>
  );
}

export function RecordSettings({ rec, className }: { rec: RoomRecorder; className?: string }) {
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState<CSSProperties>({});
  const [calibration, setCalibration] = useState<string | null>(null);
  const [calibrating, setCalibrating] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);

  const toggle = () => {
    const r = button.current?.getBoundingClientRect();
    if (r) {
      // Below the button, or above it when it sits on the bottom half (the phone's transport).
      const below = r.bottom < window.innerHeight / 2;
      const left = Math.max(8, Math.min(r.left, window.innerWidth - 264));
      setStyle(below ? { top: r.bottom + 6, left } : { bottom: window.innerHeight - r.top + 6, left });
    }
    setOpen((o) => !o);
  };

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (!panel.current?.contains(t) && !button.current?.contains(t)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("pointerdown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("pointerdown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const calibrate = async () => {
    setCalibrating(true);
    setCalibration(null);
    setCalibration(await rec.calibrate());
    setCalibrating(false);
  };

  return (
    <>
      <button
        ref={button}
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-label="Record settings"
        title="Count-in and input latency"
        className={cn("flex shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:bg-white/5", className)}
      >
        <ChevronDown className="size-3.5" />
      </button>
      {open && (
        <div
          ref={panel}
          role="dialog"
          aria-label="Record settings"
          style={style}
          className="fixed z-50 w-64 space-y-3 rounded-xl border border-panelline bg-raised p-3 text-xs shadow-xl"
        >
          <label className="flex items-center justify-between gap-2">
            <span>Count-in</span>
            <select
              value={rec.countIn}
              onChange={(e) => rec.setCountIn(Number(e.target.value))}
              className="h-7 rounded-md border border-input bg-transparent px-1"
            >
              <option value={0}>off</option>
              <option value={1}>1 bar</option>
              <option value={2}>2 bars</option>
            </select>
          </label>
          <div className="space-y-1.5">
            <label className="flex items-center justify-between gap-2">
              <span>Input latency</span>
              <span className="flex items-center gap-1">
                <input
                  type="number"
                  min={0}
                  max={500}
                  step={1}
                  value={rec.offsetMs}
                  onChange={(e) => rec.setOffsetMs(Math.max(0, Math.min(500, Number(e.target.value) || 0)))}
                  className="h-7 w-16 rounded-md border border-input bg-transparent px-1 text-right tabular-nums"
                />
                <span className="text-muted-foreground">ms</span>
              </span>
            </label>
            <p className="text-[11px] leading-snug text-muted-foreground">
              How late the mic hears the room. A take is placed this much earlier so it lines up with what you played against.
            </p>
            <button
              type="button"
              onClick={() => void calibrate()}
              disabled={calibrating || rec.recording || rec.arming}
              className="h-7 w-full rounded-md border border-panelline hover:bg-white/5 disabled:opacity-50"
              title="Plays clicks through your speakers and listens for them with the mic"
            >
              {calibrating ? "Listening…" : "Measure it"}
            </button>
            {calibration && (
              <p role="status" className="text-[11px] text-[var(--changed)]">
                {calibration}
              </p>
            )}
          </div>
        </div>
      )}
    </>
  );
}
